"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { Check as CheckCircle, X as XCircle, Activity, Loader as Loader2 } from "lucide-react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/ui/cards/card";
import { Badge } from "@/shared/ui/data-display/badge";
import { apiUrl } from "@/shared/api/config";
import { translateUi } from "@/shared/lib/ui-translate";

type EndpointStatus = "loading" | "up" | "down";

interface EndpointResult {
  id: string;
  label: string;
  path: string;
  status: EndpointStatus;
  latencyMs: number | null;
  error?: string;
}

const ENDPOINTS: { id: string; label: string; path: string }[] = [
  { id: "health", label: "litStatusPage.proverkaSostoyaniya", path: "/health" },
  { id: "health-live", label: "litStatusPage.zhivoyEndpoint", path: "/health/live" },
  { id: "health-ready", label: "litStatusPage.gotovnostKTrafiku", path: "/health/ready" },
  { id: "marketing-stats", label: "litStatusPage.publichnayaStatistika", path: "/marketing/about-stats" },
];

function humanError(raw: string): string {
  const s = (raw || "").toLowerCase();
  if (s.includes("failed to fetch") || s.includes("network")) return translateUi("litStatusPage.serverNedostupen");
  if (s.includes("timeout")) return translateUi("litStatusPage.taymautZaprosa");
  if (/^\d{3}$/.test(s.trim())) return translateUi("litStatusPage.oshibkaKod", { code: s.trim() });
  return translateUi("litStatusPage.oshibkaSoedineniya");
}

async function checkEndpoint(base: string, path: string): Promise<{ ok: boolean; latencyMs: number; error?: string }> {
  const url = `${base}${path.startsWith("/") ? path : `/${path}`}`;
  const t0 = performance.now();
  try {
    const res = await fetch(url, { cache: "no-store" });
    const latencyMs = Math.round(performance.now() - t0);
    if (res.ok) return { ok: true, latencyMs };
    return { ok: false, latencyMs, error: humanError(String(res.status)) };
  } catch (e) {
    const latencyMs = Math.round(performance.now() - t0);
    const msg = e instanceof Error ? e.message : "Ошибка";
    return { ok: false, latencyMs, error: humanError(msg) };
  }
}

function StatusBadge({ status }: { status: EndpointStatus }) {
  const tUi = useTranslations("Ui");
  if (status === "loading")
    return (
      <Badge variant="secondary" className="gap-1.5 font-normal">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        {tUi("status.proverka")}
      </Badge>
    );
  if (status === "up")
    return (
      <Badge variant="default" className="gap-1.5 bg-green-600 hover:bg-green-600 font-normal">
        <CheckCircle className="h-3.5 w-3.5" />
        {tUi("status.rabotaet")}
      </Badge>
    );
  return (
    <Badge variant="destructive" className="gap-1.5 font-normal">
      <XCircle className="h-3.5 w-3.5" />
      {tUi("status.nedostupen")}
    </Badge>
  );
}

export default function StatusPage() {
  const tUi = useTranslations("Ui");
  const t = useTranslations("Marketing.Status");
  const [results, setResults] = useState<EndpointResult[]>(
    ENDPOINTS.map((e) => ({ ...e, status: "loading" as EndpointStatus, latencyMs: null }))
  );

  useEffect(() => {
    let cancelled = false;
    const base = (apiUrl || "").replace(/\/$/, "");

    (async () => {
      for (const ep of ENDPOINTS) {
        if (cancelled) return;
        const { ok, latencyMs, error } = await checkEndpoint(base, ep.path);
        if (cancelled) return;
        setResults((prev) => {
          const copy = [...prev];
          const i = copy.findIndex((r) => r.id === ep.id);
          if (i >= 0) copy[i] = { ...ep, status: ok ? "up" : "down", latencyMs, error };
          return copy;
        });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const allUp = results.length > 0 && results.every((r) => r.status === "up");
  const anyDown = results.some((r) => r.status === "down");
  const stillLoading = results.some((r) => r.status === "loading");
  const overallStatus = stillLoading ? "loading" : anyDown ? "down" : "up";

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-12 max-w-2xl">
        <div className="mb-10 text-center">
          <Activity className="h-10 w-10 text-primary mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-foreground">{t("title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("subtitle")}</p>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                {overallStatus === "loading" && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />}
                {overallStatus === "up" && <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />}
                {overallStatus === "down" && <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />}
                {t("overall.title")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                {stillLoading
                  ? tUi("litStatus.proverkaEndpointov")
                  : allUp
                    ? tUi("litStatus.vseSistemyRabotayut")
                    : tUi("litStatus.chastServisovNedostupna")}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">{tUi("status.servisy")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border border-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/50 border-b border-border">
                      <th className="text-left font-medium text-muted-foreground py-2.5 px-3">{tUi("status.servis")}</th>
                      <th className="text-right font-medium text-muted-foreground py-2.5 px-3 w-28">{tUi("status.vremyaOtklika")}</th>
                      <th className="text-right font-medium text-muted-foreground py-2.5 px-3 w-36">{tUi("status.status")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((r) => (
                      <tr key={r.id} className="border-b border-border last:border-0">
                        <td className="py-2.5 px-3">
                          <div className="font-medium">{tUi(r.label)}</div>
                          <div className="text-xs text-muted-foreground font-mono mt-0.5">{r.path}</div>
                          {r.error && r.status === "down" && (
                            <div className="text-xs text-destructive mt-1">{r.error}</div>
                          )}
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono text-muted-foreground">
                          {r.latencyMs != null ? tUi("litStatusPage.ms", { ms: r.latencyMs }) : "—"}
                        </td>
                        <td className="py-2.5 px-3 text-right">
                          <StatusBadge status={r.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
